/**
 * How Visit history says what it counted. PURE.
 *
 * A category reads as a place a person goes ("a home improvement store"), not
 * as its label, and a date range names its year once when both ends share it.
 */
import { readsPings, type VISIT_CATEGORIES, type VisitPullInput } from '../contracts/pql';

type Category = (typeof VISIT_CATEGORIES)[number];

export const CATEGORY_PLACE: Record<Category, string> = {
    'Quick-service restaurants': 'a quick-service restaurant',
    Grocery: 'a grocery store',
    'Big-box retail': 'a big-box store',
    Pharmacy: 'a pharmacy',
    'Convenience and fuel': 'a convenience store or gas station',
    'Home improvement': 'a home improvement store',
    'Wireless carrier stores': 'a wireless carrier store',
    'Coffee shops': 'a coffee shop',
    'Department stores': 'a department store',
    'Auto dealers': 'a car dealer',
};

const MONTH = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
const YEAR = (iso: string) => iso.slice(0, 4);
const day = (iso: string) => MONTH.format(new Date(`${iso}T00:00:00Z`));

/** "Best Buy or a home improvement store": chains, then categories as places, then a saved list. */
export function placeWords(
    chains: string[],
    categories: readonly Category[],
    listName: string | null,
    join: 'and' | 'or' = 'or',
): string {
    const names = [
        ...chains,
        ...categories.map((c) => CATEGORY_PLACE[c]),
        ...(listName ? [`a store on the ${listName} list`] : []),
    ];
    return names.length <= 1
        ? (names[0] ?? '')
        : `${names.slice(0, -1).join(', ')} ${join} ${names[names.length - 1]}`;
}

/**
 * A whole calendar day, YYYY-MM-DD, that exists and is in this century: what a
 * date box holds once someone has finished typing (BT-2197: a half-typed year
 * reads as 0000 and is not one).
 */
export function isWholeDay(v: string): boolean {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(v) || v < '2000-01-01') return false;
    const d = new Date(`${v}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
}

/** "Sep 8 – Oct 7, 2026", or "Dec 20, 2025 – Jan 18, 2026" across a new year. */
export function dateRange(from: string, to: string): string {
    // A date box mid-edit holds a partial date; say so rather than format it (BT-2197).
    if (!isWholeDay(from) || !isWholeDay(to)) return 'the dates you pick';
    return YEAR(from) === YEAR(to)
        ? `${day(from)} – ${day(to)}, ${YEAR(to)}`
        : `${day(from)}, ${YEAR(from)} – ${day(to)}, ${YEAR(to)}`;
}

/** Places once each: a file often repeats a row per creative or week. */
export function uniquePlaces(points: { lat: number; lng: number }[]): number {
    return new Set(points.map((p) => `${p.lat.toFixed(6)},${p.lng.toFixed(6)}`)).size;
}

/** A file of places in a sentence: "38215.Bus.xlsx (215 places)". */
export function placesInWords(
    fileName: string | null,
    points: { lat: number; lng: number }[],
): string | null {
    if (!points.length) return null;
    return `${fileName ?? 'your file'} (${uniquePlaces(points).toLocaleString('en-US')} places)`;
}

/** The pull in one sentence, as the screen, the workbook and DANI read it. */
export function describeVisitPull(input: VisitPullInput, where: string[]): string {
    const places = `${where.slice(0, 5).join(input.chainMatch === 'all' ? ' and ' : ', ')}${where.length > 5 ? ` and ${where.length - 5} more` : ''}`;
    const radius = input.radiusMeters ? `, ${input.radiusMeters} m radius` : '';
    // No places (M4S-5904): who they are, what they did or their phone alone.
    if (!where.length)
        return input.did || input.switched
            ? `Panelists who match the filters, with what they did read between ${input.from} and ${input.to}.`
            : 'Panelists who match the filters, wherever they go.';
    if (input.presence === 'either')
        return `Passed or stayed at ${places} between ${input.from} and ${input.to}${radius}, drive-bys included.`;
    if (readsPings(input.presence))
        return `Passed ${places} between ${input.from} and ${input.to}${radius}, drive-bys included.`;
    return `Visited ${input.chainMatch === 'all' ? 'every one of ' : ''}${places} between ${input.from} and ${input.to}, staying at least ${input.dwellMinutes} minutes.`;
}
